import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, Marker, useMapEvents } from "react-leaflet";
import { BaseTiles } from "@/features/map/shared";
import L from "leaflet";
import { ArrowLeft } from "lucide-react";

import { Button } from "@/ui";
import { useCreateSpot } from "@/features/queries";
import { noAutofill } from "@/lib/noAutofill";
import type { NetType, PlaceType } from "@/types/api";

import "leaflet/dist/leaflet.css";

const MOSCOW: L.LatLngTuple = [55.7558, 37.6176];

const markerIcon = L.divIcon({
  className: "sp-admin-marker",
  iconSize: [24, 32],
  iconAnchor: [12, 32],
  html: `
    <svg width="24" height="32" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.4 0 0 5.3 0 11.8 0 20 12 32 12 32s12-12 12-20.2C24 5.3 18.6 0 12 0z" fill="#FF5A1F"/>
      <circle cx="12" cy="12" r="5" fill="#fff"/>
    </svg>`,
});

export function AdminSpotNewPage() {
  const navigate = useNavigate();
  const createMut = useCreateSpot();

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [placeType, setPlaceType] = useState<PlaceType>("outdoor_spot");
  const [netType, setNetType] = useState<NetType>("metal");
  const [hasRoof, setHasRoof] = useState(false);
  const [tablesCount, setTablesCount] = useState("1");
  const [website, setWebsite] = useState("");
  const [phone, setPhone] = useState("");
  const [position, setPosition] = useState<L.LatLngTuple | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!position) return;

    try {
      await createMut.mutateAsync({
        name: name.trim(),
        address: address.trim(),
        latitude: position[0],
        longitude: position[1],
        place_type: placeType,
        net_type: placeType === "outdoor_spot" ? netType : null,
        has_roof: placeType === "outdoor_spot" ? hasRoof : null,
        tables_count: placeType === "outdoor_spot" ? Number(tablesCount) : null,
        website: website.trim() || null,
        phone: phone.trim() || null,
      });
      navigate("/admin/spots");
    } catch {
      /* error below */
    }
  };

  return (
    <div>
      <button
        onClick={() => navigate("/admin/spots")}
        style={{
          background: "transparent", border: 0, color: "var(--fg2)", cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 6, padding: 0, marginBottom: 12,
          fontFamily: "var(--font-body)", fontSize: 13,
        }}
      >
        <ArrowLeft size={14} /> назад
      </button>

      <h1 className="h1" style={{ marginBottom: 16 }}>Новая площадка</h1>

      <div
        className="sp-admin-spot-grid"
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, alignItems: "start" }}
      >
        <form
          onSubmit={onSubmit}
          style={{
            background: "var(--bg-section)", borderRadius: "var(--r-lg)", padding: 24,
            display: "flex", flexDirection: "column", gap: 16,
          }}
        >
          <Field label="Название">
            <Input value={name} onChange={setName} required />
          </Field>

          <Field label="Адрес">
            <Input value={address} onChange={setAddress} required />
          </Field>

          <Field label="Тип">
            <select value={placeType} onChange={(e) => setPlaceType(e.target.value as PlaceType)} style={inputStyle}>
              <option value="outdoor_spot">Уличная площадка</option>
              <option value="indoor_hall">Зал</option>
              <option value="store">Магазин</option>
            </select>
          </Field>

          {placeType === "outdoor_spot" && (
            <>
              <Field label="Сетка">
                <select value={netType} onChange={(e) => setNetType(e.target.value as NetType)} style={inputStyle}>
                  <option value="metal">Металлическая</option>
                  <option value="regular">Обычная</option>
                  <option value="none">Нет сетки</option>
                </select>
              </Field>

              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={hasRoof} onChange={(e) => setHasRoof(e.target.checked)} />
                <span className="body">Есть навес</span>
              </label>

              <Field label="Количество столов">
                <Input type="number" value={tablesCount} onChange={setTablesCount} min={1} max={50} />
              </Field>
            </>
          )}

          {placeType !== "outdoor_spot" && (
            <>
              <Field label="Сайт">
                <Input value={website} onChange={setWebsite} type="url" />
              </Field>
              <Field label="Телефон">
                <Input value={phone} onChange={setPhone} type="tel" />
              </Field>
            </>
          )}

          <div style={{ fontSize: 13, color: "var(--fg2)", background: "var(--bg-secondary)", padding: 10, borderRadius: "var(--r-md)" }}>
            {position
              ? <>Координаты: <code style={{ fontFamily: "var(--font-mono)" }}>{position[0].toFixed(5)}, {position[1].toFixed(5)}</code></>
              : "Кликни по карте справа, чтобы поставить маркер"}
          </div>

          {createMut.error && (
            <div style={{
              background: "rgba(223,63,64,0.1)", color: "var(--fg-danger)",
              padding: "8px 12px", borderRadius: "var(--r-md)", fontSize: 13,
            }}>
              {(createMut.error as Error).message}
            </div>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            <Button type="submit" disabled={createMut.isPending || !name || !address || !position}>
              {createMut.isPending ? "Создаём…" : "Создать"}
            </Button>
            <Button variant="ghost" onClick={() => navigate("/admin/spots")}>Отмена</Button>
          </div>
        </form>

        <div style={{ height: 520, borderRadius: "var(--r-lg)", overflow: "hidden" }}>
          <MapContainer center={MOSCOW} zoom={11} style={{ height: "100%", width: "100%" }}>
            <BaseTiles />
            <MapClickHandler onPick={setPosition} />
            {position && <Marker position={position} icon={markerIcon} />}
          </MapContainer>
        </div>
      </div>
    </div>
  );
}

function MapClickHandler({ onPick }: { onPick: (pos: L.LatLngTuple) => void }) {
  useMapEvents({
    click(e) {
      onPick([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

const inputStyle: React.CSSProperties = {
  border: "1px solid var(--separator)",
  borderRadius: "var(--r-md)",
  padding: "10px 14px",
  fontFamily: "var(--font-body)",
  fontSize: 15,
  background: "var(--bg)",
  color: "var(--fg1)",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

// <div>, not <label>: Yandex browser swallows clicks on picker rows /
// buttons inside <label> because it delegates the click to the first
// focusable child. Composite widgets (pickers) break; plain inputs are fine
// either way. Stay safe.
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span className="label" style={{ color: "var(--fg2)" }}>{label}</span>
      {children}
    </div>
  );
}

function Input({
  value, onChange, type = "text", min, max, required,
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  min?: number;
  max?: number;
  required?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      min={min}
      max={max}
      required={required}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle}
      {...noAutofill}
    />
  );
}
